import { fromDecimals } from 'shared/lib/token/formatTokenAmount';

import { EstimateError, EstimateResult } from '../types';

export class InvalidAmountError extends Error {
  readonly type = 'AMOUNT_ERROR' as const;

  readonly kind: 'TOO_LOW' | 'TOO_HIGH';

  readonly providedAmount: bigint;

  readonly limitAmount?: bigint;

  readonly decimals: number;

  constructor(params: {
    kind: 'TOO_LOW' | 'TOO_HIGH';
    providedAmount: bigint;
    decimals?: number;
    limitAmount?: bigint;
  }) {
    const msg =
      params.kind === 'TOO_LOW'
        ? `Minimum payment amount is ${fromDecimals((params.limitAmount ?? 0).toString(), params.decimals)}`
        : 'Maximum amount exceeded';

    super(msg);

    this.name = InvalidAmountError.constructor.name;
    this.kind = params.kind;
    this.providedAmount = params.providedAmount;
    this.limitAmount = params.limitAmount;
    this.decimals = params.decimals ?? 18;
  }
}

export function validateMinPaymentAmount(amountWithDecimals: bigint, minAmount: bigint, decimals: number): void {
  if (amountWithDecimals < minAmount) {
    throw new InvalidAmountError({
      kind: 'TOO_LOW',
      providedAmount: amountWithDecimals,
      limitAmount: minAmount,
      decimals,
    });
  }
}

export function parseEstimateError(error: unknown, providedAmount: bigint): EstimateError {
  if (error instanceof InvalidAmountError) {
    return {
      type: 'AMOUNT_ERROR',
      message: error.message,
      details: {
        providedAmount: error.providedAmount,
        kind: error.kind,
        limitAmount: error.limitAmount,
      },
    };
  }

  return {
    type: 'UNKNOWN_ERROR',
    message: 'An unexpected error occurred',
    details: { providedAmount },
  };
}

export function formatEstimateResult(result: readonly [bigint, bigint, bigint]): EstimateResult {
  return {
    finalAmount: result[0],
    bonus: result[1],
    bonusPercentage: result[2],
  };
}
